import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Typography, useMediaQuery, useTheme } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import liveBoardService from "services/liveBoardService";
import {
  BoardFilters,
  BoardHub,
  FlagKey,
  LayerToggles,
  LiveBoardTrip,
  StatusKey,
  clientFacetCounts,
  clientOptions,
  computeHubs,
  defaultFilters,
  defaultLayers,
  horizonCount,
  hubFacetCounts,
  hubOptions,
  kpiCounts,
  parseTimestamp,
  scrubCoords,
  toggleFlag,
  toggleKpiStatus,
  toggleStatus,
  visibleTrips,
} from "map/liveBoard";

import LiveTripsMap from "./liveBoard/LiveTripsMap";
import KpiBar from "./liveBoard/KpiBar";
import FilterPanel from "./liveBoard/FilterPanel";
import TripList from "./liveBoard/TripList";
import TripDetailCard from "./liveBoard/TripDetailCard";
import HowToReadDialog from "./liveBoard/HowToReadDialog";

/**
 * One correction to Flow's global theme, applied only inside the board.
 *
 * Flow's own screens build buttons out of MDButton, which sets its own colours. The board uses
 * plain MUI components, which get the raw theme -- and the raw theme is wrong for them in a way
 * that renders as "the control is there but you cannot see it":
 *
 *   assets/theme/components/button/outlined.ts sets base.color to light.main (#f0f2f5) for
 *   EVERY outlined button, and its primary/secondary blocks override only borderColor and
 *   backgroundColor. So an outlined button's label is near-white on a white panel. The hub and
 *   client filters looked like empty boxes.
 *
 * The matching popover-background fix cannot live here: MUI renders a Popover into a portal on
 * document.body, outside this subtree, so a descendant selector never reaches it. It is set on
 * each Popover's own paper slot instead.
 *
 * Scoped here rather than fixed in the theme on purpose: those overrides are inherited from the
 * Material Dashboard template and every screen in Flow has been drawn against them. Correcting
 * them globally is the right long-term fix and the wrong thing to do inside a change about a
 * trip board -- the blast radius is every button and every popover in the application.
 */
const BOARD_MUI_FIXES = {
  "& .MuiButton-outlined": { color: "text.primary" },
} as const;

/**
 * Live Trips: a map of every open trip with its van, next stop, hub, late warnings and backhaul
 * opportunities, beside a list of the same trips.
 *
 * Ported from the standalone railcrew-trip-board page. This component owns the state and the
 * layout; every rule about what a trip means and what survives a filter lives in map/liveBoard.ts,
 * where it is unit-tested. Nothing here re-derives a flag.
 */

/** How often the board re-queries itself while the tab is visible. */
const REFRESH_SECONDS = 60;

/**
 * The window asked of the server, which is deliberately wider than the default 4 h shown.
 *
 * The Upcoming control is a client-side narrowing of what has already been fetched, so widening it
 * to 24 h is instant rather than a round trip. 24 is the widest the control offers.
 */
const HOURS_AHEAD = 24;

const LiveTripsBoard = () => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("lg"));

  const [trips, setTrips] = useState<LiveBoardTrip[]>([]);
  const [hubs, setHubs] = useState<BoardHub[]>([]);
  const [snapshotUtc, setSnapshotUtc] = useState<string>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>(null);

  const [filters, setFilters] = useState<BoardFilters>(defaultFilters);
  const [layers, setLayers] = useState<LayerToggles>(defaultLayers);
  const [selectedId, setSelectedId] = useState<number>(null);
  // Hover is shared between the map and the list so that pointing at either lights up the other.
  const [hoveredId, setHoveredId] = useState<number>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [fitToken, setFitToken] = useState(0);

  // Guards the auto-refresh against overlapping with a manual one, and against a response that
  // lands after the component has gone.
  const inFlight = useRef(false);
  const mounted = useRef(true);

  // On a tablet or phone the filter block is a drawer, so the trip list keeps its room.
  useEffect(() => setFiltersOpen(!isNarrow), [isNarrow]);

  const load = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const data = await liveBoardService.getBoard(HOURS_AHEAD);
      if (!mounted.current) return;
      // Scrubbed once here rather than at every read: a coordinate outside the US has to be gone
      // before anything zooms, draws or measures against it.
      const scrubbed = (data.trips || []).map(scrubCoords);
      setTrips(scrubbed);
      setHubs(computeHubs(scrubbed, data.hubs || []));
      setSnapshotUtc(data.snapshotUtc);
      setError(null);
    } catch (e) {
      if (mounted.current) setError(e?.message || "Could not load the board.");
    } finally {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load().then(() => setFitToken((n) => n + 1));
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, REFRESH_SECONDS * 1000);
    return () => {
      mounted.current = false;
      clearInterval(timer);
    };
  }, [load]);

  // A selection that a refresh has closed out has nothing left to show.
  useEffect(() => {
    if (selectedId != null && trips.length && !trips.some((t) => t.tripID === selectedId)) setSelectedId(null);
  }, [trips, selectedId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSelectedId(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const visible = useMemo(() => visibleTrips(trips, filters), [trips, filters]);
  const counts = useMemo(() => kpiCounts(trips, filters), [trips, filters]);
  const hubCounts = useMemo(() => hubFacetCounts(trips, filters), [trips, filters]);
  const clientCounts = useMemo(() => clientFacetCounts(trips, filters), [trips, filters]);
  const hubOptionList = useMemo(() => hubOptions(trips, hubs), [trips, hubs]);
  const clientOptionList = useMemo(() => clientOptions(trips), [trips]);
  const selected = useMemo(() => trips.find((t) => t.tripID === selectedId) || null, [trips, selectedId]);

  const patchFilters = (next: Partial<BoardFilters>) => setFilters((f) => ({ ...f, ...next }));
  const patchLayers = (next: Partial<LayerToggles>) => setLayers((l) => ({ ...l, ...next }));

  const reset = () => {
    setFilters(defaultFilters());
    setLayers(defaultLayers());
    setSelectedId(null);
    setFitToken((n) => n + 1);
  };

  const asOf = parseTimestamp(snapshotUtc);

  return (
    <Box sx={{ ...BOARD_MUI_FIXES, display: "flex", flexDirection: "column", height: "calc(100vh - 120px)", minHeight: 520 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, px: 1, pb: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>Live Trips</Typography>
          <Typography
            sx={{ fontSize: 11, color: "text.secondary" }}
            // Not live: the server caches for 45 s and this page re-asks every 60, so the stamp is
            // the honest answer to "how old is what I am looking at". Every "x min ago" on the
            // board is measured from it, not from now.
            title="The board is a snapshot, not a live feed: the server caches for 45 s and this page re-queries every 60 s. Refresh asks now."
          >
            {asOf
              ? `as of ${asOf.toLocaleString(undefined, {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  timeZoneName: "short",
                })}`
              : "loading…"}
            {` · pickups next ${filters.hours}h`}
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<HelpOutlineIcon />}
          onClick={() => setHelpOpen(true)}
          sx={{ textTransform: "none", fontSize: 12 }}
        >
          How to read
        </Button>
        <Button
          size="small"
          startIcon={loading ? <CircularProgress size={12} /> : <RefreshIcon />}
          onClick={() => load()}
          disabled={loading}
          sx={{ textTransform: "none", fontSize: 12 }}
          title="Re-query now"
        >
          Refresh
        </Button>
        <Box sx={{ flex: 1 }} />
        <KpiBar
          counts={counts}
          filters={filters}
          onStatus={(s) => patchFilters({ statuses: toggleKpiStatus(filters.statuses, s as StatusKey) })}
          onFlag={(f) => patchFilters({ flags: toggleFlag(filters.flags, f as FlagKey) })}
        />
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mx: 1, mb: 1, fontSize: 12 }}>
          {error}
        </Alert>
      ) : null}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          // Map beside the list on a desktop, above it on anything narrower.
          gridTemplateColumns: { xs: "1fr", lg: "1fr 420px" },
          gridTemplateRows: { xs: "minmax(280px, 45%) 1fr", lg: "1fr" },
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Box sx={{ position: "relative", minHeight: 0 }}>
          <LiveTripsMap
            trips={visible}
            hubs={hubs}
            layers={layers}
            hubFilter={filters.hubs}
            selectedId={selectedId}
            onSelect={(id) => setSelectedId((cur) => (cur === id ? null : id))}
            hoveredId={hoveredId}
            onHover={setHoveredId}
            fitToken={fitToken}
            height="100%"
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            borderLeft: { lg: 1 },
            borderTop: { xs: 1, lg: 0 },
            borderColor: { xs: "divider", lg: "divider" },
          }}
        >
          <FilterPanel
            filters={filters}
            layers={layers}
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
            onFilters={patchFilters}
            onStatusChip={(s) => patchFilters({ statuses: toggleStatus(filters.statuses, s) })}
            onLayers={patchLayers}
            onReset={reset}
            hubOptionList={hubOptionList}
            hubCounts={hubCounts}
            clientOptionList={clientOptionList}
            clientCounts={clientCounts}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1,
              py: 0.5,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
              {`${visible.length} of ${horizonCount(trips, filters.hours)} trips · next ${filters.hours}h`}
            </Typography>
            <Button size="small" onClick={reset} sx={{ ml: "auto", fontSize: 11, textTransform: "none" }}>
              Clear filters
            </Button>
          </Box>

          {selected ? (
            <Box sx={{ p: 1, borderBottom: 1, borderColor: "divider" }}>
              <TripDetailCard trip={selected} onClose={() => setSelectedId(null)} />
            </Box>
          ) : null}

          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {loading && !trips.length ? (
              <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <TripList
                trips={visible}
                selectedId={selectedId}
                hoveredId={hoveredId}
                hubFilter={filters.hubs}
                onSelect={(id) => setSelectedId((cur) => (cur === id ? null : id))}
                onHover={setHoveredId}
              />
            )}
          </Box>
        </Box>
      </Box>

      <HowToReadDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </Box>
  );
};

export default LiveTripsBoard;
